/**
 * 10.9D — DUELO PvP em TEMPO REAL (Fase 3), relay via Supabase Realtime (broadcast).
 *
 * Matchmaking DETERMINÍSTICO: os dois clientes veem a mesma lista de "na fila" (presença) e
 * calculam o MESMO par (ordenado por id, pares 0-1, 2-3…). Assim cada um sabe seu oponente sem
 * handshake, e ambos entram no MESMO canal `duel-<idA>-<idB>`.
 *
 * No duelo cada cliente é DONO do próprio lutador (posição + vida). Ele transmite seu estado
 * ~12x/s e, ao encostar no oponente, manda "hit"; quem recebe reduz a PRÓPRIA vida. Casual/relay
 * (cheatável, mas serve pro MVP). Autoritativo de verdade fica pra depois (servidor dedicado).
 */
import { supabase } from "./supabase";
import type { RealtimeChannel } from "@supabase/supabase-js";
import type { LobbyPlayer } from "./CloudPvp";

/** Par determinístico: ordena os "na fila" por id e casa 0-1, 2-3… Devolve o oponente ou null. */
export function findMatch(searchers: LobbyPlayer[], myId: string): LobbyPlayer | null {
  const sorted = searchers.filter((p) => p.searching && (p.mode ?? "1v1") === "1v1").sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
  const i = sorted.findIndex((p) => p.id === myId);
  if (i < 0) return null;
  const j = i % 2 === 0 ? i + 1 : i - 1;
  return sorted[j] ?? null;
}

// ===================== 10.9BC — 3v3 (fila aleatória + eliminação) =====================
export interface TeamMember { id: string; name: string; power: number; team: "A" | "B" }
export interface TeamRoom { topic: string; members: TeamMember[] }

/**
 * Par determinístico do 3v3: ordena os "na fila 3v3" por id e fecha SALAS de 6 (0-5, 6-11…).
 * Só devolve sala quando o meu grupo de 6 está COMPLETO. Times alternados (A,B,A,B,A,B) pela
 * ordem de id — sem handshake, os 6 clientes chegam à mesma sala/mesmos times.
 */
export function findTeamMatch(searchers: LobbyPlayer[], myId: string): TeamRoom | null {
  const sorted = searchers.filter((p) => p.searching && p.mode === "3v3").sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
  const i = sorted.findIndex((p) => p.id === myId);
  if (i < 0) return null;
  const start = i - (i % 6);
  const group = sorted.slice(start, start + 6);
  if (group.length < 6) return null;
  const members: TeamMember[] = group.map((p, k) => ({ id: p.id, name: p.name || "Caçador", power: p.power, team: k % 2 === 0 ? "A" : "B" }));
  // 10.9FF — a marca de fila (q) entra no topico: partida nova = canal novo (antes a revanche herdava o velho)
  return { topic: "team-" + group.map((p) => p.id.slice(0, 8) + (p.q ?? 0).toString(36)).join("-"), members };
}

export interface TeamState extends DuelState { id: string }
export interface TeamHandlers {
  onState: (s: TeamState) => void;
  onHit: (from: string, to: string, dmg: number) => void;
  onLeave: (id: string) => void;
}

let teamChannel: RealtimeChannel | null = null;

/** Canal da sala 3v3: estado de cada um (com id), hit direcionado (to) e presença (quem saiu). */
export function openTeamChannel(topic: string, myId: string, h: TeamHandlers): void {
  void closeTeamChannel();
  for (const c of supabase.getChannels()) {
    if (c.topic === `realtime:${topic}` || c.topic === topic) void supabase.removeChannel(c);
  }
  const ch = supabase.channel(topic, { config: { broadcast: { self: false }, presence: { key: myId } } });
  teamChannel = ch;
  try {
    ch.on("broadcast", { event: "state" }, (m) => h.onState(m.payload as TeamState));
    ch.on("broadcast", { event: "hit" }, (m) => { const p = m.payload as { from: string; to: string; dmg: number }; h.onHit(p.from, p.to, p.dmg); });
    ch.on("presence", { event: "leave" }, (m) => { for (const p of (m as { leftPresences: { id?: string }[] }).leftPresences ?? []) if (p.id) h.onLeave(p.id); });
    ch.subscribe((status) => {
      if (status === "SUBSCRIBED") void ch.track({ id: myId });
    });
  } catch (e) {
    console.warn("[team] canal indisponível", e);
  }
}
export function sendTeamState(s: TeamState): void {
  teamChannel?.send({ type: "broadcast", event: "state", payload: s });
}
export function sendTeamHit(from: string, to: string, dmg: number): void {
  teamChannel?.send({ type: "broadcast", event: "hit", payload: { from, to, dmg } });
}
export function closeTeamChannel(): Promise<void> {
  const c = teamChannel;
  teamChannel = null;
  if (!c) return Promise.resolve();
  try {
    return supabase.removeChannel(c).then(() => undefined, () => undefined);
  } catch {
    return Promise.resolve();
  }
}

export interface DuelState {
  x: number;
  z: number;
  hp: number;
  maxHp: number;
  f?: number; // 10.9AH — facing (rad) p/ a arena 3D (opcional; compatível com clientes antigos)
  id?: string; // 10.9FF — quem mandou (ignora eco do proprio estado)
  a?: boolean; // 10.9AH — atacando neste instante (dispara a animação de golpe no oponente)
  /** 10.9HK — LOOK da arma (visualId, energia, tier, família) pro rival me ver como sou */
  w?: { v: string; e?: number; t?: number; f?: string };
}

export interface DuelHandlers {
  onState: (s: DuelState) => void;
  onHit: (dmg: number) => void;
  onEnd: (loserId: string) => void;
  onOpponentLeft: () => void;
}

let channel: RealtimeChannel | null = null;

/** Entra no canal do duelo (nome derivado dos dois ids, ordenado). */
export function openDuelChannel(myId: string, oppId: string, h: DuelHandlers, matchKey = ""): void {
  // 10.9FF — matchKey (marcas de fila dos dois) deixa cada partida num canal NOVO. Antes a revanche
  // contra o mesmo rival reusava o topico e o "saiu" do canal antigo encerrava a luta nova na hora.
  const name = "duel-" + [myId, oppId].sort().join("-") + (matchKey ? "-" + matchKey : "");
  // 10.9AW — BUG do 1x1: em revanche/2ª partida o supabase-js REAPROVEITAVA o canal já inscrito
  // com o mesmo tópico e `.on()` estourava ("cannot add presence callbacks after subscribe") —
  // o duelo abortava antes de renderizar (tela preta "Aguardando…"). Agora removemos QUALQUER
  // canal antigo com esse tópico (e o atual) antes de criar um novo; os handlers ficam num
  // canal fresco. A remoção é assíncrona, mas `removeChannel` já tira o canal da lista do
  // cliente de forma síncrona o bastante pra `channel(name)` criar um novo.
  void closeDuelChannel();
  for (const c of supabase.getChannels()) {
    if (c.topic === `realtime:${name}` || c.topic === name) void supabase.removeChannel(c);
  }
  const ch = supabase.channel(name, { config: { broadcast: { self: false } } });
  channel = ch;
  duelMe = myId;
  try {
    ch.on("broadcast", { event: "state" }, (m) => { const s = m.payload as DuelState; if (s.id !== myId) h.onState(s); }); // 10.9FF — ignora eco
    ch.on("broadcast", { event: "hit" }, (m) => { const p = m.payload as { dmg: number; from?: string }; if (p.from !== myId) h.onHit(p.dmg); });
    ch.on("broadcast", { event: "end" }, (m) => h.onEnd((m.payload as { loserId: string }).loserId));
    // 10.9FF — so conta como "saiu" se quem saiu foi o OPONENTE (nao reconexao minha)
    ch.on("presence", { event: "leave" }, (m) => {
      const left = (m as { leftPresences?: { id?: string }[] }).leftPresences ?? [];
      if (left.some((p) => p.id === oppId)) h.onOpponentLeft();
    });
    ch.subscribe((status) => {
      if (status === "SUBSCRIBED") void ch.track({ id: myId });
    });
  } catch (e) {
    // nunca deixa a arena morrer por causa do canal: sem rede o duelo vira "aguardando" e expira
    console.warn("[duel] canal indisponível", e);
  }
}

export function sendDuelState(s: DuelState): void {
  channel?.send({ type: "broadcast", event: "state", payload: s });
}
let duelMe = ""; // 10.9FF
export function sendDuelHit(dmg: number): void {
  channel?.send({ type: "broadcast", event: "hit", payload: { dmg, from: duelMe } });
}
export function sendDuelEnd(loserId: string): void {
  channel?.send({ type: "broadcast", event: "end", payload: { loserId } });
}

export function closeDuelChannel(): Promise<void> {
  const c = channel;
  channel = null;
  if (!c) return Promise.resolve();
  try {
    return supabase.removeChannel(c).then(() => undefined, () => undefined);
  } catch {
    return Promise.resolve();
  }
}
